/**
 * Animation Tokens for EroChat Design System
 *
 * Provides consistent animation durations, easing functions,
 * and transform values across the application.
 *
 * @module design-system/tokens/animation
 */

/**
 * Animation token values interface
 */
export interface AnimationTokens {
  /** Fast duration - micro-interactions (150ms) */
  durationFast: string;
  /** Normal duration - standard transitions (300ms) */
  durationNormal: string;
  /** Slow duration - complex animations (500ms) */
  durationSlow: string;
  /** Extra slow duration - page transitions (700ms) */
  durationExtraSlow: string;
  /** Default easing - smooth deceleration */
  easingDefault: string;
  /** Bounce easing - playful overshoot effect */
  easingBounce: string;
  /** Spring easing - natural spring physics */
  easingSpring: string;
  /** Ease in - acceleration */
  easingIn: string;
  /** Ease out - deceleration */
  easingOut: string;
  /** Ease in-out - smooth acceleration and deceleration */
  easingInOut: string;
  /** Hover scale - slight enlargement on hover */
  hoverScale: number;
  /** Press scale - slight shrink on press/active */
  pressScale: number;
  /** Focus scale - scale for focus states */
  focusScale: number;
}

/**
 * Default animation values for consistent motion design.
 *
 * Duration values follow a consistent scale:
 * - Fast (150ms): Button clicks, toggles, micro-interactions
 * - Normal (300ms): Most UI transitions, hover effects
 * - Slow (500ms): Modals, page sections, complex reveals
 * - Extra Slow (700ms): Page transitions, hero animations
 *
 * @example
 * ```tsx
 * import { defaultAnimation } from '@/design-system/tokens/animation';
 *
 * const Button = () => (
 *   <button
 *     style={{
 *       transition: `transform ${defaultAnimation.durationFast} ${defaultAnimation.easingDefault}`,
 *     }}
 *     className="hover:scale-[1.02] active:scale-[0.98]"
 *   >
 *     Click me
 *   </button>
 * );
 * ```
 */
export const defaultAnimation: AnimationTokens = {
  /**
   * Fast duration (150ms)
   * Best for: button clicks, toggles, icon transitions
   */
  durationFast: '150ms',

  /**
   * Normal duration (300ms)
   * Best for: hover effects, dropdowns, most UI transitions
   */
  durationNormal: '300ms',

  /**
   * Slow duration (500ms)
   * Best for: modals, sheets, complex animations
   */
  durationSlow: '500ms',

  /**
   * Extra slow duration (700ms)
   * Best for: page transitions, hero animations, onboarding
   */
  durationExtraSlow: '700ms',

  /**
   * Default easing - Material Design standard
   * Starts fast, ends slow - feels responsive
   */
  easingDefault: 'cubic-bezier(0.4, 0, 0.2, 1)',

  /**
   * Bounce easing - playful overshoot
   * Creates a slight bounce at the end
   * Best for: fun interactions, notifications, achievements
   */
  easingBounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',

  /**
   * Spring easing - natural spring physics
   * Smoother than bounce, more organic feel
   * Best for: modals, sliding panels, drag & drop
   */
  easingSpring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',

  /**
   * Ease in - acceleration
   * Starts slow, ends fast
   * Best for: elements leaving the screen
   */
  easingIn: 'cubic-bezier(0.4, 0, 1, 1)',

  /**
   * Ease out - deceleration
   * Starts fast, ends slow
   * Best for: elements entering the screen
   */
  easingOut: 'cubic-bezier(0, 0, 0.2, 1)',

  /**
   * Ease in-out - smooth both ways
   * Symmetric acceleration and deceleration
   * Best for: elements that stay on screen (transforms)
   */
  easingInOut: 'cubic-bezier(0.4, 0, 0.6, 1)',

  /**
   * Hover scale (1.02 = 102%)
   * Subtle enlargement for hover states
   */
  hoverScale: 1.02,

  /**
   * Press scale (0.98 = 98%)
   * Subtle shrink for pressed/active states
   */
  pressScale: 0.98,

  /**
   * Focus scale (1.01 = 101%)
   * Very subtle scale for focus states
   */
  focusScale: 1.01,
};

/**
 * Helper to get an animation CSS variable reference
 *
 * @param key - Animation token key
 * @returns CSS variable reference string
 *
 * @example
 * ```tsx
 * const duration = getAnimationVar('durationFast');
 * // Returns: 'var(--animation-duration-fast)'
 * ```
 */
export const getAnimationVar = (key: keyof AnimationTokens): string => {
  const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
  const variableName = key === 'easingDefault' ? 'easing' : kebabKey;
  return `var(--animation-${variableName})`;
};

/**
 * Converts animation tokens to CSS custom properties
 *
 * @param animation - Animation tokens object
 * @returns Object with CSS variable names as keys
 *
 * @example
 * ```ts
 * const cssVars = animationToCssVars(defaultAnimation);
 * // { '--animation-duration-fast': '150ms', ... }
 * ```
 */
export const animationToCssVars = (animation: AnimationTokens): Record<string, string> => {
  return Object.entries(animation).reduce<Record<string, string>>((acc, [key, value]) => {
    const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
    const variableName = key === 'easingDefault' ? 'easing' : kebabKey;
    acc[`--animation-${variableName}`] = String(value);
    return acc;
  }, {});
};

/**
 * Pre-built transition strings for common use cases
 *
 * @example
 * ```tsx
 * import { transitions } from '@/design-system/tokens/animation';
 *
 * <button style={{ transition: transitions.interactive }}>
 *   Hover me
 * </button>
 * ```
 */
export const transitions = {
  /** Fast transition for colors and opacity */
  color: `color ${defaultAnimation.durationFast} ${defaultAnimation.easingDefault}, background-color ${defaultAnimation.durationFast} ${defaultAnimation.easingDefault}`,

  /** Transform transition for scale, rotate, translate */
  transform: `transform ${defaultAnimation.durationFast} ${defaultAnimation.easingDefault}`,

  /** Standard interactive element transition */
  interactive: `transform ${defaultAnimation.durationFast} ${defaultAnimation.easingDefault}, box-shadow ${defaultAnimation.durationFast} ${defaultAnimation.easingDefault}, background-color ${defaultAnimation.durationFast} ${defaultAnimation.easingDefault}`,

  /** Opacity transition for fade effects */
  opacity: `opacity ${defaultAnimation.durationNormal} ${defaultAnimation.easingDefault}`,

  /** All properties - use sparingly */
  all: `all ${defaultAnimation.durationNormal} ${defaultAnimation.easingDefault}`,

  /** Modal/sheet enter transition */
  modal: `transform ${defaultAnimation.durationSlow} ${defaultAnimation.easingSpring}, opacity ${defaultAnimation.durationNormal} ${defaultAnimation.easingOut}`,
} as const;
